// File: frontend/src/pages/SuperAdmin/components/SystemSettings/index.js

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLanguage, 
  faList, 
  faGlobeAmericas, 
  faExclamationCircle, 
  faCheckCircle 
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import LanguageManagement from './LanguageManagement';
import CategoryManagement from './CategoryManagement';
import GeographyManagement from './GeographyManagement';
import styles from './SystemSettings.module.css';

const SystemSettings = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('language');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Parse URL query parameters to determine active tab and search terms
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    
    if (tab && ['language', 'categories', 'geography'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Update URL when tab changes
  const updateUrlParams = (tab) => {
    const params = new URLSearchParams();
    if (tab) params.set('tab', tab);
    
    navigate({
      pathname: '/super-admin-panel/settings',
      search: params.toString()
    }, { replace: true });
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
    setSuccess(null);
    updateUrlParams(tab);
  };

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>{t('system_settings')}</h1>
      
      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${activeTab === 'language' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('language')}
        >
          <FontAwesomeIcon icon={faLanguage} />
          <span>{t('language_management')}</span>
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'categories' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('categories')}
        >
          <FontAwesomeIcon icon={faList} />
          <span>{t('category_management')}</span>
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'geography' ? styles.activeTab : ''}`}
          onClick={() => handleTabChange('geography')}
        >
          <FontAwesomeIcon icon={faGlobeAmericas} />
          <span>{t('geography_management')}</span>
        </button>
      </div>
      
      {error && (
        <div className={styles.error}>
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{error}</span>
          <button 
            className={styles.closeMessage} 
            onClick={() => setError(null)}
            aria-label="Close error message"
          >
            ×
          </button>
        </div>
      )}
      
      {success && (
        <div className={styles.success}>
          <FontAwesomeIcon icon={faCheckCircle} />
          <span>{success}</span>
          <button 
            className={styles.closeMessage} 
            onClick={() => setSuccess(null)}
            aria-label="Close success message"
          >
            ×
          </button>
        </div>
      )}
      
      <div className={styles.tabContent}>
        {/* Language Management Tab */}
        {activeTab === 'language' && (
          <LanguageManagement 
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
            success={success}
            setSuccess={setSuccess}
          />
        )}
        
        {/* Category Management Tab */}
        {activeTab === 'categories' && (
          <CategoryManagement 
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
            success={success}
            setSuccess={setSuccess}
          />
        )}
        
        {/* Geography Management Tab */}
        {activeTab === 'geography' && (
          <GeographyManagement 
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
            success={success}
            setSuccess={setSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default SystemSettings;